/**
 * Squarified Treemap Layout Algorithm
 *
 * Lays out rectangles proportional to size values within a container.
 * Uses the squarified algorithm (Bruls, Huizing, van Wijk 2000)
 * to produce rectangles with aspect ratios close to 1.
 *
 * @module @humanizer/studio/utils/treemap
 */

export interface TreemapItem {
  name: string;
  size: number;
  isDirectory: boolean;
  path: string;
  mtime: string;
}

export interface TreemapRect extends TreemapItem {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function worstAspectRatio(row: number[], shortSide: number, totalArea: number): number {
  if (row.length === 0) return Infinity;
  const rowSum = row.reduce((a, b) => a + b, 0);
  const rowMax = Math.max(...row);
  const rowMin = Math.min(...row);
  const s2 = shortSide * shortSide;
  // worst = max(rowMax * s^2 / rowSum^2, rowSum^2 / (rowMin * s^2))
  return Math.max(
    (rowMax * s2) / (rowSum * rowSum),
    (rowSum * rowSum) / (rowMin * s2)
  );
}

function layoutRow(
  row: number[],
  items: TreemapItem[],
  container: Rect,
  isHorizontal: boolean,
  results: TreemapRect[]
): Rect {
  const rowSum = row.reduce((a, b) => a + b, 0);

  if (isHorizontal) {
    const rowWidth = container.w > 0 ? rowSum / container.h : 0;
    let y = container.y;
    for (let i = 0; i < row.length; i++) {
      const h = rowWidth > 0 ? row[i] / rowWidth : 0;
      results.push({
        ...items[i],
        x: container.x,
        y,
        w: rowWidth,
        h,
      });
      y += h;
    }
    return {
      x: container.x + rowWidth,
      y: container.y,
      w: container.w - rowWidth,
      h: container.h,
    };
  } else {
    const rowHeight = container.h > 0 ? rowSum / container.w : 0;
    let x = container.x;
    for (let i = 0; i < row.length; i++) {
      const w = rowHeight > 0 ? row[i] / rowHeight : 0;
      results.push({
        ...items[i],
        x,
        y: container.y,
        w,
        h: rowHeight,
      });
      x += w;
    }
    return {
      x: container.x,
      y: container.y + rowHeight,
      w: container.w,
      h: container.h - rowHeight,
    };
  }
}

/**
 * Compute a squarified treemap layout.
 *
 * @param items - Items to layout, each with a size value
 * @param width - Container width in pixels
 * @param height - Container height in pixels
 * @returns Array of items with x, y, w, h coordinates
 */
export function computeTreemap(
  items: TreemapItem[],
  width: number,
  height: number
): TreemapRect[] {
  if (items.length === 0 || width <= 0 || height <= 0) return [];

  // Filter zero-size items, sort descending by size
  const sorted = items
    .filter(item => item.size > 0)
    .sort((a, b) => b.size - a.size);

  if (sorted.length === 0) {
    // All items have zero size — give them equal space
    const equalSize = 1;
    const equalItems = items.map(item => ({ ...item, size: equalSize }));
    return computeTreemap(equalItems, width, height);
  }

  const totalSize = sorted.reduce((sum, item) => sum + item.size, 0);
  const totalArea = width * height;

  // Normalize sizes to areas
  const areas = sorted.map(item => (item.size / totalSize) * totalArea);

  const results: TreemapRect[] = [];
  let container: Rect = { x: 0, y: 0, w: width, h: height };
  let row: number[] = [];
  let rowItems: TreemapItem[] = [];
  let i = 0;

  while (i < sorted.length) {
    const shortSide = Math.min(container.w, container.h);
    const isHorizontal = container.w >= container.h;

    if (row.length === 0) {
      row.push(areas[i]);
      rowItems.push(sorted[i]);
      i++;
      continue;
    }

    const currentWorst = worstAspectRatio(row, shortSide, totalArea);
    const withNew = [...row, areas[i]];
    const newWorst = worstAspectRatio(withNew, shortSide, totalArea);

    if (newWorst <= currentWorst) {
      // Adding improves aspect ratio
      row.push(areas[i]);
      rowItems.push(sorted[i]);
      i++;
    } else {
      // Lay out current row
      container = layoutRow(row, rowItems, container, isHorizontal, results);
      row = [];
      rowItems = [];
    }
  }

  // Lay out remaining row
  if (row.length > 0) {
    const isHorizontal = container.w >= container.h;
    layoutRow(row, rowItems, container, isHorizontal, results);
  }

  return results;
}

/**
 * Get a color class name based on file extension or directory status.
 */
export function getFileTypeColor(name: string, isDirectory: boolean): string {
  if (isDirectory) return 'treemap-rect--directory';

  const ext = name.split('.').pop()?.toLowerCase() || '';

  // Images
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return 'treemap-rect--image';
  }
  // Audio
  if (['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'].includes(ext)) {
    return 'treemap-rect--audio';
  }
  // Video
  if (['mp4', 'webm', 'avi', 'mov', 'mkv', 'wmv'].includes(ext)) {
    return 'treemap-rect--video';
  }
  // Archives
  if (['zip', 'tar', 'gz', 'bz2', '7z', 'rar'].includes(ext)) {
    return 'treemap-rect--archive';
  }
  // Text/data
  if (['json', 'csv', 'txt', 'md', 'xml', 'html', 'yml', 'yaml', 'log'].includes(ext)) {
    return 'treemap-rect--text';
  }

  return 'treemap-rect--other';
}
